/**
 * Analytics identity wiring for the organisation panel (PIX-605).
 *
 * The panel counterpart to identity.js, and the seam that was simply missing:
 * identify and reset lived only in the student auth store, so a teacher in the
 * panel was either an anonymous person or - on a shared school machine -
 * inherited the distinct id of whatever student was logged in on the same
 * browser. No per-teacher metric was possible at all.
 *
 * Deliberately logic-free, the same way identity.js is. The identity rule lives
 * in identitySwitch.js and the property derivation in orgEventProperties.js,
 * both pure and separately tested, because this is where a silent error would
 * do the most damage: nothing throws when a school's activity is filed under
 * the wrong school.
 *
 * IDENTIFIER: the panel login response keys the account on `uuid`, the same
 * namespace as the student payload - teachers, administrators and students are
 * all rows in one user table. So a person who is both resolves to one analytics
 * person for free, with no aliasing and no bridge.
 *
 * ONE ENTRY POINT. Login, user fetch and organisation switch all call
 * `identifyOrganisationUser`. It is idempotent by design, so the store never
 * has to reason about which of those it is doing.
 */
import { analytics } from './analytics.js';
import { GROUPS, PROPS } from './events.js';
import { decideIdentityAction, IDENTITY_ACTIONS } from './identitySwitch.js';
import {
  deriveOrgSuperProperties,
  deriveSelectedOrganisation,
} from './orgEventProperties.js';

/** Environment label, matching how identity.js labels student events. */
function environmentLabel() {
  return import.meta.env.VITE_APP_ENVIRONMENT || 'development';
}

/**
 * Point the organisation group at the school currently being acted on.
 *
 * A falsy key is a no-op at the facade, which covers both logout and the
 * window before a multi-school user has chosen which school they are in.
 */
function associateSelectedOrganisation(organisations, selectedOrganisationId) {
  const selected = deriveSelectedOrganisation({
    organisations,
    selectedOrganisationId,
  });
  if (!selected?.uuid) return;

  analytics.associateGroup(GROUPS.ORGANISATION, selected.uuid, {
    name: selected.name,
  });
}

/**
 * Associate subsequent events with a panel user and refresh their context.
 *
 * Safe to call repeatedly, and called from every seam that can change any of
 * its inputs - login, panel user fetch, and organisation switch. Re-deriving
 * on each is how a role change or a switch between schools stops tagging
 * events with stale context.
 *
 * The identify call is gated by the switch rule; the property registration and
 * group association are not. Super properties must refresh even when the
 * identity is unchanged, or switching schools would never reach the events -
 * a teacher at one school may be an administrator at another.
 */
export function identifyOrganisationUser({
  user = null,
  organisations = [],
  selectedOrganisationId = null,
} = {}) {
  const nextId = user?.uuid;
  if (!nextId) return;

  const action = decideIdentityAction({
    currentIdentifiedId: analytics.identifiedDistinctId(),
    nextId,
  });

  // Severing first is what stops two known people merging into one person on a
  // shared machine. It costs a new session, so it happens only on a genuine
  // change of person - never on a re-identify or a hop between surfaces.
  if (action === IDENTITY_ACTIONS.RESET_THEN_IDENTIFY) {
    analytics.reset();
  }
  if (action !== IDENTITY_ACTIONS.NOOP) {
    analytics.identify(nextId);
  }

  analytics.registerSuperProperties(
    deriveOrgSuperProperties({
      user,
      organisations,
      selectedOrganisationId,
      environment: environmentLabel(),
    })
  );

  associateSelectedOrganisation(organisations, selectedOrganisationId);
}

/**
 * Attribute events to a school without identifying anyone (PIX-626).
 *
 * For the forced set-password page, which is unauthenticated: its only
 * credential is a token in the URL, so there is no panel user to identify and
 * no membership list to derive from. The organisation uuid comes from the
 * token-check response instead.
 *
 * A group association does not require an identified person, which is what
 * makes this possible at all - and it is the only way the invited-to-password-
 * set half of the seat-expansion funnel gets a school attached, since that is
 * the step where invitations most often quietly die.
 *
 * A falsy uuid is a no-op: the backend returns null rather than guessing when
 * the invitee holds memberships at more than one school.
 */
export function associateInvitationOrganisation(organisationUuid) {
  if (!organisationUuid) return;
  analytics.associateGroup(GROUPS.ORGANISATION, organisationUuid);
}

/**
 * Clear panel identity and accumulated context. Call on panel logout.
 *
 * Re-seeds only authentication state and the environment. Unlike the student
 * reset there is no board or subscription baseline to restore, and surface is
 * re-registered by the router binding on the next navigation.
 */
export function resetOrganisationIdentity() {
  analytics.reset();
  analytics.registerSuperProperties({
    [PROPS.IS_AUTHENTICATED]: false,
    [PROPS.APP_ENVIRONMENT]: environmentLabel(),
  });
}
